import { compose, find, map, pathOr, propEq, propOr, omit, path } from 'ramda';
import * as mcc from 'mcc';

import { ApolloError } from 'apollo-boost';
import { GraphQLError } from 'graphql';

import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';
import {
  TermUnits,
  BudgetType,
  AdvertiserType,
  FinancialTermItem,
  FinancialTermItemInput,
  DataByKey,
  SegmentType,
  PromotionType,
  CouponType,
  OfferNetworkType,
  RedemptionType,
  OfferScopeType,
  VenueType,
  SpecialVenueType,
  TargetingRuleType,
  TargetingRule,
} from './MerchantOffer.types';

const capitalize = (s: string) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const DefaultOptions = {
  options: [{ key: 'select', value: '', text: 'Select' }],
  label: {
    children: '',
  },
  placeholder: '',
};

const ThrottleOptions = {
  options: [
    { key: 'select', value: null, text: 'No Throttle Attached' },
    {
      key: 'existing',
      value: 'existing',
      text: 'Attach existing Throttle',
    },
  ],
  label: {
    children: 'Throttle',
  },
  placeholder: 'No Throttle Attached',
};

const SegmentTypes = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    {
      key: 'base',
      value: SegmentType.BASE,
      text: 'Base',
    },
    { key: 'targeted', value: SegmentType.TARGETED, text: 'Targeted' },
    { key: 'mug', value: SegmentType.MUG, text: SegmentType.MUG },
  ],
  label: {
    children: 'Segment Type',
  },
  placeholder: 'Select Segment Type',
};

const PromotionTypes = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    {
      key: PromotionType.STANDARD,
      value: PromotionType.STANDARD,
      text: 'Standard',
    },
    {
      key: PromotionType.CASH_FLASH,
      value: PromotionType.CASH_FLASH,
      text: 'Cash Flash',
    },
  ],
  label: {
    children: 'Promotion Type',
  },
  placeholder: 'Select Promotion Type',
};

const VenueTypes = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    {
      key: VenueType.ONLINE,
      text: capitalize(VenueType.ONLINE),
      value: VenueType.ONLINE,
    },
    {
      key: VenueType.NEARBY,
      text: capitalize(VenueType.NEARBY),
      value: VenueType.NEARBY,
    },
    {
      key: VenueType.ALL,
      text: capitalize(VenueType.ALL),
      value: VenueType.ALL,
    },
    {
      key: SpecialVenueType.NO_LOCATIONS,
      text: 'No Locations',
      value: SpecialVenueType.NO_LOCATIONS,
    },
  ],
  label: {
    children: 'Venue Type',
  },
  placeholder: 'Select Venue Type',
};

const CouponTypeOptions = {
  options: [
    { key: 'select', value: 'null', text: 'Select' },
    {
      key: CouponType.STANDARD,
      value: CouponType.STANDARD,
      text: capitalize(CouponType.STANDARD),
    },
    {
      key: CouponType.LOYALTY,
      value: CouponType.LOYALTY,
      text: capitalize(CouponType.LOYALTY),
    },
    {
      key: CouponType.METERED,
      value: CouponType.METERED,
      text: capitalize(CouponType.METERED),
    },
  ],
  label: {
    children: 'Coupon Type',
  },
  placeholder: 'Select Coupon Type',
};

const OfferNetworksOptions = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    {
      key: OfferNetworkType.DOSH,
      value: OfferNetworkType.DOSH,
      text: capitalize(OfferNetworkType.DOSH),
    },
    {
      key: OfferNetworkType.VENMO,
      value: OfferNetworkType.VENMO,
      text: capitalize(OfferNetworkType.VENMO),
    },
  ],
  label: {
    children: 'Offer Network',
  },
  placeholder: 'Select Offer Network',
};

const RedemptionTypeOptions = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    {
      key: RedemptionType.LINKED_CARD,
      value: RedemptionType.LINKED_CARD,
      text: capitalize(RedemptionType.LINKED_CARD),
    },
    {
      key: RedemptionType.QR_CODE,
      value: RedemptionType.QR_CODE,
      text: capitalize(RedemptionType.QR_CODE),
    },
  ],
  label: {
    children: 'Redemption Type',
  },
  placeholder: 'Select Redemption Type',
};

const OfferScopeTypes = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    {
      key: OfferScopeType.MARKETPLACE,
      value: OfferScopeType.MARKETPLACE,
      text: capitalize(OfferScopeType.MARKETPLACE),
    },
    {
      key: OfferScopeType.VENUE,
      value: OfferScopeType.VENUE,
      text: capitalize(OfferScopeType.VENUE),
    },
  ],
  label: {
    children: 'Offer Scope Type',
  },
  placeholder: 'Select Offer Scope Type',
};

const FinancialTermUnitTypes = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    {
      key: TermUnits.PERCENTAGE,
      text: 'Percentage',
      value: TermUnits.PERCENTAGE,
    },
    { key: TermUnits.USD, text: 'USD', value: TermUnits.USD },
  ],
  label: {
    children: 'Financial Term Unit',
  },
  placeholder: 'Select Unit Type',
};

const MarketplaceFunded = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    { key: 'true', text: 'True', value: true },
    { key: 'false', text: 'False', value: false },
  ],
  label: {
    children: 'Marketplace Funded',
  },
  placeholder: 'Is Marketplace funded?',
};

const TermUnitsOptions = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    {
      key: TermUnits.PERCENTAGE,
      value: TermUnits.PERCENTAGE,
      text: 'Percentage Offer',
    },
    {
      key: TermUnits.USD,
      value: TermUnits.USD,
      text: 'Dollar Amount Offer',
    },
  ],
  label: {
    children: '',
  },
  placeholder: 'Select Offer Type',
};

const MerchantCategoryCodes = {
  options: mcc.all.map((codeInfo: any) => {
    return {
      value: codeInfo.mcc,
      label: `${codeInfo.combined_description}:${codeInfo.mcc}`,
    };
  }),
  label: {
    children: 'Merchant Category',
  },
  placeholder: 'Select Merchant Category',
};

const BudgetTypes = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    { key: 'fixed', text: 'Fixed', value: `${BudgetType.FIXED_AMOUNT}` },
    { key: 'open', text: 'Open', value: `${BudgetType.OPEN}` },
    {
      key: 'monthly',
      text: 'Monthly',
      value: `${BudgetType.FIXED_AMOUNT_FIXED_DATE_RANGE}`,
    },
  ],
  label: {
    children: 'Budget Type',
  },
  placeholder: 'Select Budget Type',
};

const AdvertiserTypeOptions = {
  options: [
    { key: 'select', value: null, text: 'Select' },
    { key: 'merchant', text: 'Merchant', value: `${AdvertiserType.MERCHANT}` },
    {
      key: 'marketplace',
      text: 'Marketplace',
      value: `${AdvertiserType.MARKETPLACE}`,
    },
  ],
  label: {
    children: 'Advertiser Type',
  },
  placeholder: 'Select Advertiser Type',
};

const DisplayLabels = {
  FinancialTermLabels: {
    CAMPAIGN_FEE: 'Campaign Fee',
    MARKETING_FEE: 'Marketing Fee',
    CASHBACK: 'Cash Back',
  },
  PromotionTypeLabels: {
    STANDARD: 'Standard',
    CASH_FLASH: 'Cash Flash',
  },
};

const defaultPriorityByAudienceExternalID = (
  audienceUUID: string,
): number | null => {
  if (audienceUUID.includes('LAPSED_USER')) {
    return 100;
  }
  if (audienceUUID.includes('COMPETITIVE_CATEGORIES_USER')) {
    return 75;
  }
  if (audienceUUID.includes('LOYAL_USER')) {
    return 50;
  }
  if (audienceUUID.includes('NEW_USER')) {
    return 25;
  }
  return null;
};

const filterTargetingRules = ({ segment, audience }: any) => {
  const targetingRulesFilter =
    audience !== null ? Boolean : (rule: any) => rule.type !== 'AUDIENCE';
  const targetingRules = omitTypename(segment).filter(targetingRulesFilter);
  return targetingRules;
};

const buildTargetingRules = (
  mugId: string | number,
  audienceId: string | null,
  throttleId: string | null,
  merchantId: string,
  segmentType: SegmentType,
  targettingRule: TargetingRule,
): {
  type: SegmentType | TargetingRuleType;
  dataByKey: Record<string, string[] | unknown>;
  data: (string | number)[];
}[] => {
  const rules = [];
  if (segmentType === SegmentType.BASE) {
    rules.push({
      type: segmentType,
      dataByKey: {},
      data: ['1'],
    });
  }
  if (audienceId) {
    rules.push({
      type: TargetingRuleType.AUDIENCE,
      dataByKey: {
        AUDIENCE_UUID: [audienceId],
      },
      data: [audienceId],
    });
  }
  if (mugId) {
    rules.push({
      type: SegmentType.MUG,
      dataByKey: {
        MUG_UUID: [mugId],
      },
      data: [mugId],
    });
  }
  if (throttleId && merchantId) {
    rules.push({
      type: TargetingRuleType.OFFER_THROTTLE_V1,
      dataByKey: {
        HASHMOD_SALT: [path(['HASHMOD_SALT', 0], targettingRule.dataByKey)],
        OFFER_THROTTLE_UUID: [throttleId],
      },
      data: [merchantId, throttleId],
    });
  }
  return rules;
};

const getMarketingFee = (FinancialTermItems: FinancialTermItem[]): string =>
  compose<any, any, string>(
    propOr('0', 'termValue'),
    find(propEq('termType', 'MARKETING_FEE')),
  )(FinancialTermItems);

const omitTypename = (segment: any) =>
  compose(map(omit(['__typename'])), pathOr([], ['targetingRules']))(segment);

const buildFinancialTerms = (
  financialTermItems: FinancialTermItem[],
  cashbackPercentage: number,
  cashbackAmount: number,
  marketingFee: number,
): FinancialTermItemInput[] => {
  const newFinancialTermItems: FinancialTermItemInput[] = [];
  const termUnits = cashbackPercentage ? TermUnits.PERCENTAGE : TermUnits.USD;
  const cashbackValue = cashbackPercentage
    ? cashbackPercentage
    : cashbackAmount;

  financialTermItems.map((financialTermItem: FinancialTermItem) => {
    let termValue = 0;
    switch (financialTermItem.termType) {
      case 'MARKETING_FEE':
        termValue = marketingFee;
        break;
      case 'CASHBACK':
        termValue = cashbackValue;
        break;
      case 'CAMPAIGN_FEE':
        termValue = marketingFee + cashbackValue;
        break;
    }

    const item = {
      termValue,
      termUnits,
      termType: financialTermItem.termType,
      payerType: financialTermItem.payerType,
      budgetId: financialTermItem.budgetId,
    };
    newFinancialTermItems.push(item);
  });
  return newFinancialTermItems;
};

const getApolloError = (errors?: ApolloError | GraphQLError[]) =>
  pathOr('', ['graphQLErrors'], errors);

const handleError = (error: any): void => {
  toastMapping({}, 'error')(getApolloError(error) as any);
};

const handleSuccess = (success: any) =>
  toastMapping({}, 'success')(success as any);

/**
 * Checks if the user has entered Minimun Spend or Exact Spend but not both.
 * @param minSpend Minimun Spending
 * @param exactSpend Exact Spending
 * @returns true if one of them has been filled out
 */
const checkMinimumOrExactSpend = (
  minSpend: string,
  exactSpend: [{ name: string }],
): boolean =>
  (parseFloat(minSpend) > 0 &&
    exactSpend.filter((exactSpend) => exactSpend.name !== '').length === 0) || // Due to how we initialize `exactSpend` we ignore it's content
  (minSpend.length === 0 &&
    exactSpend.length > 0 &&
    exactSpend.map((exactSpend) => exactSpend.name).every(parseFloat));

export {
  SegmentTypes,
  PromotionTypes,
  FinancialTermUnitTypes,
  MerchantCategoryCodes,
  DisplayLabels,
  DefaultOptions,
  VenueTypes,
  MarketplaceFunded,
  BudgetTypes,
  DataByKey,
  buildTargetingRules,
  filterTargetingRules,
  getMarketingFee,
  omitTypename,
  buildFinancialTerms,
  handleError,
  handleSuccess,
  defaultPriorityByAudienceExternalID,
  ThrottleOptions,
  AdvertiserTypeOptions,
  TermUnitsOptions,
  CouponTypeOptions,
  OfferNetworksOptions,
  RedemptionTypeOptions,
  OfferScopeTypes,
  checkMinimumOrExactSpend,
};
